"use client";

import { animated, useSpring, useTransition } from "@react-spring/web";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { ChevronRight, ClipboardCheck } from "lucide-react";
import Link from "next/link";
import { memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pushEvent } from "@v2/lib/analytics";
import {
    AdvisorCandidate,
    AdvisorCta,
    AdvisorData,
    AdvisorQuestion,
    AdvisorResult,
    AdvisorState,
    candidatesForTier,
    eligibleQuestions,
    isUsableQuestion,
    matchResult,
    resolveState,
    whyLines,
} from "./advisorRules";

type AdvisorLayout = "split" | "stacked";
type ProgressPosition = "top" | "bottom";

type ViewportSettings = {
    visible: boolean;
    layout: AdvisorLayout;
    progress_position: ProgressPosition;
};

type AdvisorColorsType = {
    background: string | null;
    card: string | null;
    border: string | null;
    panel: string | null;
    accent: string | null;
    title: string | null;
    text: string | null;
    muted: string | null;
    option_bg: string | null;
    option_border: string | null;
    selected_bg: string | null;
    action_bg: string | null;
    action_fg: string | null;
};

type CustomStyleDeclarations = Record<string, string | number>;

// Same three-viewport shape as `block`. A viewport entry may hold plain
// declarations, which style the component root, and/or a documented advisor-*
// class name mapped to its own declarations, which styles that part.
type CustomStyleGroup = Record<string, string | number | CustomStyleDeclarations>;

type AdvisorCustomStyleType = {
    mobile?: CustomStyleGroup;
    tablet?: CustomStyleGroup;
    desktop?: CustomStyleGroup;
};

type AdvisorLCSettingsType = {
    label: string | null;
    max_width: string | null;
    anchor_id?: string | null;
    intro: {
        title: string;
        description: string | null;
        bullets: string[];
        note: string | null;
        panel_title: string | null;
        panel_description: string | null;
        start_text: string;
    };
    flow: {
        show_counter: boolean;
        counter_template: string;
        show_progress: boolean;
        allow_back: boolean;
        back_text: string;
        digits: "fa" | "en";
        persist: "none" | "session";
    };
    result: {
        eyebrow: string | null;
        show_reset: boolean;
        reset_text: string;
        note: string | null;
        show_why: boolean;
        why_title: string | null;
        why_limit: number;
        points_title: string | null;
        show_alternative: boolean;
        alternative_title: string | null;
        candidates_title: string | null;
    };
    analytics: {
        enabled: boolean;
        event_prefix: string | null;
    };
    colors: Partial<AdvisorColorsType>;
    custom_style?: AdvisorCustomStyleType;
    mobile?: Partial<ViewportSettings>;
    tablet?: Partial<ViewportSettings>;
    desktop?: Partial<ViewportSettings>;
};

const AnimatedBox = animated(Box);

const DEFAULT_VIEWPORT: ViewportSettings = { visible: true, layout: "split", progress_position: "bottom" };

const COLORS: AdvisorColorsType = {
    background: '#F3EDE9',
    card: '#ffffff',
    border: '#E4DAD3',
    panel: '#FBF9F7',
    accent: '#BB202E',
    title: '#191513',
    text: '#5C534D',
    muted: '#8B7F76',
    option_bg: '#ffffff',
    option_border: '#DED5CE',
    selected_bg: '#FDF6F6',
    action_bg: '#191513',
    action_fg: '#ffffff',
};

const TRACK_COLOR = '#EDE5E0';
const OPTION_FG = '#3B3430';
const RESET_BORDER = '#DED5CE';

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

// Screens cross-fade in place. They are clipped by the panel while its height
// animates, so any offset — sideways into the card edge, or downward into the
// clip — shows up as a sliced edge. The height change carries the motion.
const LEAVE_MS = 160;
const ENTER_MS = 220;
// Just under LEAVE_MS: a hair of overlap at near-zero opacity beats a blank beat.
const ENTER_DELAY_MS = 140;
const HEIGHT_MS = 280;
// The intro column folding away as the run starts.
const COLLAPSE_MS = 320;
// Going back to the start screen is a cascade rather than a cross-fade: the panel
// gives up the result's height, then the start screen arrives in the space that
// leaves, then the intro column unfolds beside a card that has stopped moving.
// Three separate motions, each waiting for the one before it.
const RESET_ENTER_DELAY_MS = HEIGHT_MS + 40;
const REVEAL_DELAY_MS = RESET_ENTER_DELAY_MS + ENTER_MS + 40;

// How wide the panel content is allowed to get once it has the whole card. Wide
// enough that the card does not read as a narrow column with empty margins either
// side of it, short enough that a paragraph keeps a sane line length.
const PANEL_MEASURE = 780;

// The class that names the component root. Targeting it from custom_style has
// to style the root element itself rather than a descendant.
const ROOT_CLASS = 'advisor-root';

// Hover styling is opt-in per device: on touch screens a :hover rule sticks to
// the last tapped element and makes an answered option look still-hovered.
const HOVER_QUERY = '@media (hover: hover)';

const STORAGE_PREFIX = 'advisor:';

function localizeDigits(value: number | string, digits: "fa" | "en") {
    const text = String(value);
    if (digits === 'en') return text;
    return text.replace(/[0-9]/g, d => PERSIAN_DIGITS[Number(d)]);
}

type Stage = 'idle' | 'active' | 'done';

// Breakpoint keys the three stored viewports map to.
type BreakpointKey = 'xs' | 'sm2' | 'lg';
type Responsive<T> = Record<BreakpointKey, T>;

const VIEWPORTS = ['mobile', 'tablet', 'desktop'] as const;

const CSS_PROPERTY = /^[a-zA-Z][a-zA-Z0-9]*$/;
const CSS_CLASS = /^[a-zA-Z][a-zA-Z0-9_-]*$/;

/**
 * Turns the stored custom_style into one sx object.
 *
 * The viewport shape and the forward cascade match `block`: a value set only on
 * mobile keeps applying upward until a later viewport overrides it. On top of
 * that, a viewport entry whose value is an object is read as a class name and
 * becomes a nested selector, which is how the inner parts of the component are
 * reachable at all.
 */
function makeCustomStyleSx(style?: AdvisorCustomStyleType) {
    if (!style) return {};

    // selector -> viewport -> declarations
    const bySelector: Record<string, Record<string, CustomStyleDeclarations>> = {};

    const put = (selector: string, viewport: string, declarations: CustomStyleDeclarations) => {
        bySelector[selector] = bySelector[selector] ?? { mobile: {}, tablet: {}, desktop: {} };
        Object.entries(declarations).forEach(([property, value]) => {
            if (typeof value !== 'string' && typeof value !== 'number') return;
            if (!CSS_PROPERTY.test(property)) return;
            bySelector[selector][viewport][property] = value;
        });
    };

    VIEWPORTS.forEach(viewport => {
        const group = style[viewport];
        if (!group || typeof group !== 'object' || Array.isArray(group)) return;

        Object.entries(group).forEach(([key, value]) => {
            if (typeof value === 'string' || typeof value === 'number') {
                // Plain declaration: styles the root, exactly like `block` does.
                put('&', viewport, { [key]: value });
                return;
            }
            if (!value || typeof value !== 'object' || Array.isArray(value)) return;
            if (!CSS_CLASS.test(key)) return;
            put(key === ROOT_CLASS ? '&' : `& .${key}`, viewport, value as CustomStyleDeclarations);
        });
    });

    return Object.fromEntries(Object.entries(bySelector).map(([selector, viewportStyles]) => {
        const properties = new Set([
            ...Object.keys(viewportStyles.mobile),
            ...Object.keys(viewportStyles.tablet),
            ...Object.keys(viewportStyles.desktop),
        ]);

        const declarations = [...properties].map(property => {
            const mobileValue = viewportStyles.mobile[property];
            const tabletValue = viewportStyles.tablet[property] ?? mobileValue;
            const desktopValue = viewportStyles.desktop[property] ?? tabletValue;
            return [property, { xs: mobileValue, sm2: tabletValue, lg: desktopValue }];
        });

        return [selector, Object.fromEntries(declarations)];
    }));
}

/**
 * A stable per-advisor storage key.
 *
 * Two advisors can sit on one page, so the key cannot be a constant. The anchor
 * id is the one author-set identifier that is already unique per page; without
 * one the intro headline stands in, hashed so the key stays short and ASCII.
 */
function storageKey(settings: AdvisorLCSettingsType): string {
    if (settings.anchor_id) return `${STORAGE_PREFIX}${settings.anchor_id}`;

    const source = settings.intro?.title ?? '';
    let hash = 0;
    for (let index = 0; index < source.length; index += 1) {
        hash = ((hash << 5) - hash + source.charCodeAt(index)) | 0;
    }
    return `${STORAGE_PREFIX}${(hash >>> 0).toString(36)}`;
}

type StoredProgress = {
    stage: Stage;
    path: string[];
    answers: Record<string, string>;
};

const AdvisorLC = memo(function AdvisorLC({ content, disableLinks }: {
    content: {
        settings: AdvisorLCSettingsType;
        data: AdvisorData | null;
    };
    disableLinks?: boolean;
}) {
    const { settings } = content;

    const data = useMemo<AdvisorData>(() => ({
        dimensions: content.data?.dimensions ?? [],
        tiers: content.data?.tiers ?? [],
        questions: content.data?.questions ?? [],
        rules: content.data?.rules ?? { strong_gap: 3, low_confidence_max: 0, tie_breaker_question: null, combos: [] },
        results: content.data?.results ?? [],
    }), [content.data]);

    // A question with one option cannot be answered and a hidden one is not part
    // of the test, so neither ever reaches the path, the counter, or the panel.
    const questions = useMemo(() => data.questions.filter(isUsableQuestion), [data.questions]);
    const results = useMemo(() => data.results.filter(result => result?.show_in_site !== false), [data.results]);

    const byKey = useMemo(() => {
        const map: Record<string, AdvisorQuestion> = {};
        questions.forEach(question => { map[question.key] = question; });
        return map;
    }, [questions]);

    const [stage, setStage] = useState<Stage>('idle');
    // The questions actually walked, in order. A branch the visitor backed out
    // of leaves the path, so its answer stops counting towards the scores.
    const [path, setPath] = useState<string[]>([]);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    // The panel preview and the studio frame render the same component with
    // links disabled. Neither is a visit, so neither measures or remembers.
    const isLive = !disableLinks;

    const viewports = useMemo<Responsive<ViewportSettings>>(() => ({
        xs: { ...DEFAULT_VIEWPORT, ...settings.mobile },
        sm2: { ...DEFAULT_VIEWPORT, ...settings.tablet },
        lg: { ...DEFAULT_VIEWPORT, ...settings.desktop },
    }), [settings.mobile, settings.tablet, settings.desktop]);

    const colors = useMemo<AdvisorColorsType>(() => {
        const overrides = settings.colors ?? {};
        const merged = { ...COLORS };
        (Object.keys(COLORS) as (keyof AdvisorColorsType)[]).forEach(key => {
            if (overrides[key]) merged[key] = overrides[key] as string;
        });
        return merged;
    }, [settings.colors]);

    const customStyleSx = useMemo(() => makeCustomStyleSx(settings.custom_style), [settings.custom_style]);

    const dimensionByKey = useMemo(() => {
        const map: Record<string, { label: string; description: string | null }> = {};
        data.dimensions.forEach(dimension => {
            map[dimension.key] = { label: dimension.label, description: dimension.description ?? null };
        });
        return map;
    }, [data.dimensions]);

    /** The state as it stood before the question at `index` in the path was answered. */
    const stateAt = useCallback((index: number, currentAnswers = answers, currentPath = path): AdvisorState => (
        resolveState(data, currentPath.slice(0, index).map(key => byKey[key]).filter(Boolean), currentAnswers)
    ), [data, byKey, answers, path]);

    /** The state once every answered question in the path is counted. */
    const finalState = useMemo(
        () => resolveState(data, path.map(key => byKey[key]).filter(Boolean), answers),
        [data, byKey, path, answers],
    );

    // The last result is the fallback when nothing matched. A stored advisor is
    // supposed to end with an unconditional entry for exactly this reason, but a
    // visitor must never reach a blank screen because an author forgot one.
    const answeredQuestions = useMemo(
        () => path.map(key => byKey[key]).filter(Boolean),
        [path, byKey],
    );

    const matchedResult = useMemo(
        () => matchResult(data, finalState) ?? results[results.length - 1] ?? null,
        [data, finalState, results],
    );

    const track = useCallback((event: string, params: Record<string, unknown> = {}) => {
        if (!isLive) return;
        if (settings.analytics?.enabled === false) return;

        const prefix = settings.analytics?.event_prefix?.trim();
        if (!prefix) return;

        pushEvent(`${prefix}_${event}`, params);
    }, [isLive, settings.analytics]);

    const firstQuestion = useCallback((currentAnswers: Record<string, string>) => {
        const empty = resolveState(data, [], currentAnswers);
        return eligibleQuestions(questions, empty)[0] ?? null;
    }, [data, questions]);

    const start = useCallback(() => {
        const first = firstQuestion({});
        if (!first) return;

        // The very first paint of the start screen is not a return to it, and must
        // not sit through a delay meant for one.
        hasStarted.current = true;

        setAnswers({});
        setPath([first.key]);
        setStage('active');
        track('start');
    }, [firstQuestion, track]);

    const reset = useCallback(() => {
        setStage('idle');
        setPath([]);
        setAnswers({});
        track('cta_click', { cta_type: 'restart' });
    }, [track]);

    const back = useCallback(() => {
        setPath(previous => (previous.length > 1 ? previous.slice(0, -1) : previous));
    }, []);

    const answer = useCallback((question: AdvisorQuestion, optionKey: string) => {
        const index = path.indexOf(question.key);
        if (index < 0) return;

        // Answering again after going back invalidates whatever came after: the
        // branch that follows may not be the same one any more.
        const walked = path.slice(0, index + 1);
        const nextAnswers = { ...answers, [question.key]: optionKey };

        const state = resolveState(data, walked.map(key => byKey[key]).filter(Boolean), nextAnswers);
        const masterIndex = questions.findIndex(item => item.key === question.key);
        const next = eligibleQuestions(questions, state, masterIndex + 1)[0] ?? null;

        setAnswers(nextAnswers);
        setPath(next ? [...walked, next.key] : walked);
        setStage(next ? 'active' : 'done');

        track('answer', {
            question_id: question.key,
            answer_id: optionKey,
            step_number: index + 1,
        });
    }, [answers, byKey, data, path, questions, track]);

    // Completion is reported once per run. Without the guard a re-render on the
    // result screen — a resize, a theme change — would send the event again and
    // the funnel would show more completions than visitors.
    const reportedRun = useRef<string | null>(null);

    useEffect(() => {
        if (stage !== 'done' || !matchedResult) {
            if (stage !== 'done') reportedRun.current = null;
            return;
        }

        const signature = `${matchedResult.id}:${path.join('>')}`;
        if (reportedRun.current === signature) return;
        reportedRun.current = signature;

        track('complete', { step_number: path.length });
        track('result', {
            primary_route: finalState.primary,
            secondary_route: finalState.secondary,
            modifier: finalState.modifier,
            quality_tier: finalState.tier,
            low_confidence: finalState.low_confidence,
            ...finalState.flags,
        });
    }, [stage, matchedResult, finalState, path, track]);

    const persist = settings.flow?.persist === 'session' && isLive;
    const restored = useRef(false);

    // Restored after mount, never during render: the server has no session
    // storage, so reading it in the first render would hand React two different
    // trees for the same markup.
    useEffect(() => {
        if (!persist || restored.current) return;
        restored.current = true;

        try {
            const raw = sessionStorage.getItem(storageKey(settings));
            if (!raw) return;

            const stored = JSON.parse(raw) as StoredProgress;
            const storedPath = (stored?.path ?? []).filter(key => byKey[key]);
            // A stored path is only worth restoring whole: an admin who deleted
            // one of its questions has changed the test the visitor was taking.
            if (storedPath.length !== (stored?.path ?? []).length) return;
            if (stored?.stage === 'active' && !storedPath.length) return;

            setAnswers(stored?.answers ?? {});
            setPath(storedPath);
            setStage(stored?.stage === 'active' || stored?.stage === 'done' ? stored.stage : 'idle');
        } catch {
            // A blocked or full session storage is not a reason to lose the advisor.
        }
    }, [persist, settings, byKey]);

    useEffect(() => {
        if (!persist || !restored.current) return;

        try {
            if (stage === 'idle') sessionStorage.removeItem(storageKey(settings));
            else sessionStorage.setItem(storageKey(settings), JSON.stringify({ stage, path, answers }));
        } catch {
            // Same as above: storage is a convenience here, never a dependency.
        }
    }, [persist, settings, stage, path, answers]);

    // The panel follows its content instead of being padded out to the tallest
    // screen. Measured rather than reserved, so a short question is short — and
    // animated, so the resize is part of the transition rather than a snap
    // between two sizes.
    const contentRef = useRef<HTMLDivElement>(null);
    const [contentHeight, setContentHeight] = useState<number | null>(null);
    const measured = useRef(false);

    useEffect(() => {
        const element = contentRef.current;
        if (!element || typeof ResizeObserver === 'undefined') return;

        const observer = new ResizeObserver(entries => {
            const entry = entries[0];
            const height = entry?.borderBoxSize?.[0]?.blockSize ?? element.offsetHeight;
            if (height) setContentHeight(height);
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (contentHeight !== null) measured.current = true;
    }, [contentHeight]);

    const hasStarted = useRef(false);

    // What the result screen was showing when it was reached, held so that the
    // screen on its way out keeps rendering the run the visitor actually took.
    const [shownResult, setShownResult] = useState<{
        result: AdvisorResult;
        state: AdvisorState;
        answered: AdvisorQuestion[];
    } | null>(null);

    useEffect(() => {
        if (stage !== 'done' || !matchedResult) return;
        setShownResult({ result: matchedResult, state: finalState, answered: answeredQuestions });
    }, [stage, matchedResult, finalState, answeredQuestions]);

    // Two flags rather than one derived from `stage`, because the column folds and
    // unfolds on different schedules. `introOpen` is the track and the fade;
    // `introMounted` is whether the column still counts towards the card height.
    const [introOpen, setIntroOpen] = useState(true);
    const [introMounted, setIntroMounted] = useState(true);

    useEffect(() => {
        if (stage !== 'idle') {
            // Folding away starts the moment the visitor answers: there is nothing
            // to wait for, the panel is about to need the room.
            setIntroOpen(false);
            if (reduceMotion) {
                setIntroMounted(false);
                return;
            }
            // Out of the height calculation only once it has finished fading, or
            // the copy is clipped away instead of fading.
            const timer = setTimeout(() => setIntroMounted(false), COLLAPSE_MS);
            return () => clearTimeout(timer);
        }

        const reveal = () => {
            setIntroMounted(true);
            setIntroOpen(true);
        };

        if (reduceMotion) {
            reveal();
            return;
        }

        // Coming back is the other way round: the screens cross-fade and the panel
        // settles at its start-screen height first, and only then does the column
        // unfold into a card that has stopped moving.
        const timer = setTimeout(reveal, REVEAL_DELAY_MS);
        return () => clearTimeout(timer);
    }, [stage, reduceMotion]);

    const heightSpring = useSpring({
        height: contentHeight ?? 0,
        // The first measurement must land without animating up from zero.
        immediate: reduceMotion || !measured.current,
        config: { duration: HEIGHT_MS },
    });

    const activeKey = path[path.length - 1] ?? null;
    const activeQuestion = stage === 'active' && activeKey ? byKey[activeKey] ?? null : null;

    // Which screen is on show. Every transition is keyed off this one string.
    const currentKey = stage === 'idle'
        ? 'idle'
        : stage === 'active'
            ? `q-${activeQuestion?.key ?? ''}`
            : `r-${matchedResult?.id ?? 0}`;

    // The incoming screen mounts straight away and waits, invisible, until the
    // outgoing one has faded. That reads like exitBeforeEnter without its side
    // effect: exitBeforeEnter leaves a beat with no screen mounted at all, and
    // wherever nothing reserves the height the panel collapses through it.
    // Durations rather than a spring so the hand-off timing is exact.
    const transitions = useTransition(currentKey, {
        from: { opacity: 0 },
        // Everywhere but the way back, the incoming screen waits only for the
        // outgoing one to fade. Returning to the start screen waits for the panel
        // height as well, so the two do not compete.
        enter: (item: string) => ({
            opacity: 1,
            delay: reduceMotion
                ? 0
                : (item === 'idle' && hasStarted.current ? RESET_ENTER_DELAY_MS : ENTER_DELAY_MS),
        }),
        leave: { opacity: 0 },
        config: (_item, _index, phase) => ({ duration: phase === 'leave' ? LEAVE_MS : ENTER_MS }),
        immediate: reduceMotion,
    });

    if (!questions.length || !results.length) return null;
    if (!viewports.xs.visible && !viewports.sm2.visible && !viewports.lg.visible) return null;

    // Every layout difference is emitted as a breakpoint-keyed sx value rather
    // than branched in JS. A media-query hook would resolve to the mobile
    // layout during SSR and the first client render, so a desktop visitor would
    // see the stacked layout paint and then jump once hydration corrected it.
    const by = <T,>(pick: (viewport: ViewportSettings, key: BreakpointKey) => T): Responsive<T> => ({
        xs: pick(viewports.xs, 'xs'),
        sm2: pick(viewports.sm2, 'sm2'),
        lg: pick(viewports.lg, 'lg'),
    });
    const isStacked = (viewport: ViewportSettings) => viewport.layout === 'stacked';

    const { intro, flow, result: resultSettings } = settings;
    const digits = flow?.digits === 'en' ? 'en' : 'fa';
    const showProgress = flow?.show_progress !== false;
    const maxWidth = settings.max_width ? `${settings.max_width}px` : undefined;

    /**
     * How many questions this run holds, as far as anyone can honestly say.
     *
     * A conditional question is counted only once its condition holds, so the
     * total can grow when an answer opens a branch. Promising a fixed total
     * would mean either over-counting questions the visitor will never see or
     * claiming the test is finished while a branch is still ahead.
     */
    const stepsFor = (question: AdvisorQuestion) => {
        const index = path.indexOf(question.key);
        const walked = index < 0 ? path.length : index + 1;
        const state = stateAt(Math.max(index, 0));
        const masterIndex = questions.findIndex(item => item.key === question.key);
        const remaining = eligibleQuestions(questions, state, masterIndex + 1).length;

        return { current: walked, total: walked + remaining };
    };

    const counterFor = (question: AdvisorQuestion) => {
        const { current, total } = stepsFor(question);
        return (flow?.counter_template || 'سوال {current} از {total}')
            .replace('{current}', localizeDigits(current, digits))
            .replace('{total}', localizeDigits(total, digits));
    };

    const BULLET_CLASS: Record<'intro' | 'result' | 'why', string> = {
        intro: 'advisor-intro-bullet',
        result: 'advisor-result-point',
        why: 'advisor-result-why-line',
    };

    const bullet = (text: string, key: string | number, variant: 'intro' | 'result' | 'why') => (
        <Box
            key={key}
            className={`advisor-bullet ${BULLET_CLASS[variant]}`}
            sx={{
                display: 'flex',
                gap: 1.25,
                alignItems: variant === 'intro' ? 'center' : 'flex-start',
                fontSize: { xs: 14, lg: 14.5 },
                color: OPTION_FG,
                lineHeight: 1.85,
            }}
        >
            <Box className="advisor-bullet-dot" sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: colors.accent,
                flex: 'none',
                mt: variant === 'intro' ? 0 : '8px',
            }} />
            {text}
        </Box>
    );

    const progressBar = (value: number) => (
        <Box
            className="advisor-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={value}
            sx={{ height: 4, borderRadius: '99px', bgcolor: TRACK_COLOR, overflow: 'hidden' }}
        >
            <Box className="advisor-progress-fill" sx={{ height: '100%', width: `${value}%`, bgcolor: colors.accent, transition: 'width .3s' }} />
        </Box>
    );

    const startButton = (display: Responsive<string> | string) => (
        <Box
            component="button"
            type="button"
            className="advisor-start"
            onClick={start}
            sx={{
                display,
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                width: '100%',
                minHeight: by(v => isStacked(v) ? 58 : 56),
                px: 3.5,
                border: 'none',
                borderRadius: '8px',
                bgcolor: colors.action_bg,
                color: colors.action_fg,
                fontFamily: 'inherit',
                fontSize: 16,
                fontWeight: 600,
                // Without this the label carries the font's full leading while the
                // icon does not, and the two sit on different centre lines.
                lineHeight: 1,
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
                transition: 'transform .12s ease, filter .18s ease',
                '&:active': { transform: 'scale(0.99)' },
                [HOVER_QUERY]: { '&:hover': { filter: 'brightness(1.25)' } },
                // The icon inherits the button's colour explicitly: a bare svg would
                // fall back to whatever a global rule set and disappear on the dark
                // button.
                '& svg': { display: 'block', flex: 'none', color: 'inherit', stroke: 'currentColor' },
            }}
        >
            <ClipboardCheck className="advisor-start-icon" size={20} strokeWidth={2} aria-hidden />
            <Box component="span" className="advisor-start-label">{intro.start_text}</Box>
        </Box>
    );

    const introContent = (
        <>
            <Typography component="h2" className="advisor-intro-title" sx={{
                fontSize: by((v, key) => isStacked(v) ? 22 : (key === 'lg' ? 30 : 26)),
                fontWeight: 700,
                color: colors.title,
                letterSpacing: '-.4px',
                lineHeight: 1.5,
                m: 0,
            }}>
                {intro.title}
            </Typography>

            {intro.description && (
                <Typography className="advisor-intro-description" sx={{ fontSize: { xs: 14.5, lg: 15 }, color: colors.text, lineHeight: 1.9, mt: 1.5 }}>
                    {intro.description}
                </Typography>
            )}

            {intro.bullets?.length > 0 && (
                <Box className="advisor-intro-bullets" sx={{ display: 'grid', gap: 1.5, mt: 2.75 }}>
                    {intro.bullets.map((text, index) => bullet(text, index, 'intro'))}
                </Box>
            )}

            {/* Stacked has no side panel, so the start button belongs to the intro
                flow there; the split layout keeps its own copy in the panel. */}
            <Box className="advisor-intro-start" sx={{ display: by(v => isStacked(v) ? 'block' : 'none'), mt: 2.75 }}>
                {startButton('flex')}
            </Box>

            {intro.note && (
                <Typography className="advisor-intro-note" sx={{
                    fontSize: 12.5,
                    color: colors.muted,
                    lineHeight: 1.8,
                    mt: by(v => isStacked(v) ? 1.5 : 2.75),
                }}>
                    {intro.note}
                </Typography>
            )}
        </>
    );

    const idlePanel = (
        <>
            {/* Stacked has no second column, so the intro itself is the start screen
                and travels through the same transition as the questions. */}
            <Box className="advisor-panel-intro-stacked" sx={{ display: by(v => isStacked(v) ? 'block' : 'none') }}>
                {introContent}
            </Box>

            {/* The copy stays optically centred while the button is pinned to the
                bottom edge, where the next-step control belongs. */}
            <Box className="advisor-panel-intro" sx={{ flex: 1, display: by(v => isStacked(v) ? 'none' : 'flex'), flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 2.25, pb: 3 }}>
                {intro.panel_title && (
                    <Typography className="advisor-panel-title" sx={{ fontSize: 19, fontWeight: 700, color: colors.title, lineHeight: 1.7 }}>
                        {intro.panel_title}
                    </Typography>
                )}
                {intro.panel_description && (
                    <Typography className="advisor-panel-description" sx={{ fontSize: 14.5, color: colors.text, lineHeight: 1.9 }}>
                        {intro.panel_description}
                    </Typography>
                )}
            </Box>
            {startButton(by(v => isStacked(v) ? 'none' : 'flex'))}
        </>
    );

    const questionPanel = (question: AdvisorQuestion) => {
        const { current, total } = stepsFor(question);
        const progress = total > 0 ? Math.round(((current - 1) / total) * 100) : 0;
        const canGoBack = flow?.allow_back !== false && path.indexOf(question.key) > 0;

        return (
            <>
                {(flow?.show_counter !== false || flow?.allow_back !== false) && (
                    <Box className="advisor-header" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                        {/* Back sits on the start side and the counter on the end side, so
                            the control the visitor reaches for stays where reading starts. */}
                        {flow?.allow_back !== false ? (
                            <Box
                                component="button"
                                type="button"
                                className="advisor-back"
                                onClick={back}
                                // Faded rather than visibility:hidden. The screen behind a
                                // transition is dimmed by its parent, and a descendant
                                // setting visibility:visible would punch straight through
                                // that — opacity cannot escape its parent's.
                                sx={{
                                    opacity: canGoBack ? 1 : 0,
                                    pointerEvents: canGoBack ? 'auto' : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    background: 'none',
                                    border: 'none',
                                    p: 0.5,
                                    fontFamily: 'inherit',
                                    fontSize: 13.5,
                                    color: colors.muted,
                                    cursor: 'pointer',
                                    transition: 'color .18s',
                                    WebkitTapHighlightColor: 'transparent',
                                    '& svg': { display: 'block', flex: 'none' },
                                    [HOVER_QUERY]: { '&:hover': { color: colors.title } },
                                }}
                            >
                                {/* Physical direction, not mirrored: in RTL the previous
                                    question lies toward the right. */}
                                <ChevronRight className="advisor-back-icon" size={16} strokeWidth={2} aria-hidden />
                                {flow?.back_text || 'سوال قبل'}
                            </Box>
                        ) : <span />}

                        {flow?.show_counter !== false && (
                            <Box className="advisor-counter" sx={{ fontSize: 13.5, fontWeight: 500, color: colors.muted }}>
                                {counterFor(question)}
                            </Box>
                        )}
                    </Box>
                )}

                {showProgress && (
                    <Box className="advisor-progress-slot advisor-progress-slot-top" sx={{ display: by(v => v.progress_position === 'top' ? 'block' : 'none'), mt: 1.25 }}>
                        {progressBar(progress)}
                    </Box>
                )}

                <Typography
                    id={`advisor-question-${question.key}`}
                    className="advisor-question"
                    sx={{
                        fontSize: by(v => isStacked(v) ? 18 : 17),
                        fontWeight: 600,
                        color: colors.title,
                        lineHeight: 1.8,
                        mt: by(v => isStacked(v) ? 2.25 : 1.75),
                        minHeight: by(v => isStacked(v) ? 'auto' : 60),
                    }}
                >
                    {question.title}
                </Typography>

                {question.description && (
                    <Typography className="advisor-question-description" sx={{ fontSize: 13.5, color: colors.muted, lineHeight: 1.8, mt: 1 }}>
                        {question.description}
                    </Typography>
                )}

                {/* A radio group rather than a row of buttons: a screen reader then
                    announces "2 of 4" and the arrow keys move between the answers,
                    which is what a questionnaire is expected to do. */}
                <Box
                    className="advisor-options"
                    role="radiogroup"
                    aria-labelledby={`advisor-question-${question.key}`}
                    sx={{ display: 'grid', gap: by(v => isStacked(v) ? 1.25 : 1), mt: by(v => isStacked(v) ? 2 : 0.75) }}
                >
                    {question.options.map(option => {
                        const selected = answers[question.key] === option.key;
                        return (
                            <Box
                                key={option.id}
                                component="button"
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                className={`advisor-option${selected ? ' advisor-option-selected' : ''}`}
                                onClick={() => answer(question, option.key)}
                                sx={{
                                    textAlign: 'start',
                                    fontFamily: 'inherit',
                                    p: by(v => isStacked(v) ? 2 : '14px 16px'),
                                    minHeight: by(v => isStacked(v) ? 56 : 50),
                                    borderRadius: '8px',
                                    fontSize: by(v => isStacked(v) ? 15 : 14.5),
                                    // Constant weight: switching to bold on selection
                                    // reflows the label and the text visibly jumps.
                                    fontWeight: 400,
                                    cursor: 'pointer',
                                    WebkitTapHighlightColor: 'transparent',
                                    touchAction: 'manipulation',
                                    transition: 'transform .12s ease, border-color .18s, background-color .18s, color .18s',
                                    borderStyle: 'solid',
                                    borderWidth: '1px',
                                    borderColor: selected ? colors.accent : colors.option_border,
                                    bgcolor: selected ? colors.selected_bg : colors.option_bg,
                                    color: selected ? colors.accent : OPTION_FG,
                                    // Pressing is confirmed by the tap itself, before the
                                    // screen transitions — the one moment a touch visitor
                                    // gets any feedback at all.
                                    '&:active': {
                                        transform: 'scale(0.985)',
                                        borderColor: colors.accent,
                                        bgcolor: colors.selected_bg,
                                        color: colors.accent,
                                    },
                                    [HOVER_QUERY]: {
                                        '&:hover': { borderColor: colors.accent, bgcolor: colors.selected_bg },
                                    },
                                }}
                            >
                                {option.label}
                            </Box>
                        );
                    })}
                </Box>

                {showProgress && (
                    <Box className="advisor-progress-slot advisor-progress-slot-bottom" sx={{ display: by(v => v.progress_position === 'top' ? 'none' : 'block'), mt: by(v => isStacked(v) ? 2 : 'auto'), pt: 2 }}>
                        {progressBar(progress)}
                    </Box>
                )}
            </>
        );
    };

    const ctaButton = (cta: AdvisorCta, resultColor: string) => {
        const isPrimary = cta.variant !== 'secondary';
        const linked = Boolean(cta.link) && !disableLinks;

        return (
            <Box
                key={cta.id}
                component={linked ? Link : 'div'}
                href={linked ? cta.link as string : undefined}
                className={`advisor-result-cta${isPrimary ? '' : ' advisor-result-cta-secondary'}`}
                onClick={() => track('cta_click', {
                    cta_type: cta.cta_type ?? null,
                    primary_route: finalState.primary,
                    quality_tier: finalState.tier,
                })}
                sx={{
                    // Grows to fill only where it is alone on the row. Beside a
                    // second CTA and the reset, a button that grows pushes the
                    // reset onto a line of its own however wide the panel gets.
                    flex: by(v => isStacked(v) ? '1 1 auto' : '0 1 auto'),
                    minWidth: 0,
                    px: 2.5,
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 52,
                    borderRadius: '8px',
                    bgcolor: isPrimary ? colors.accent : 'transparent',
                    border: isPrimary ? 'none' : `1px solid ${resultColor}`,
                    color: isPrimary ? '#fff' : resultColor,
                    fontSize: 15,
                    fontWeight: 600,
                    lineHeight: 1.6,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    transition: 'transform .12s ease, filter .18s ease',
                    '&:active': { transform: 'scale(0.99)' },
                    [HOVER_QUERY]: { '&:hover': { filter: 'brightness(1.1)' } },
                }}
            >
                {cta.text}
            </Box>
        );
    };

    const candidateCard = (candidate: AdvisorCandidate) => {
        const linked = Boolean(candidate.link) && !disableLinks;

        return (
            <Box
                key={candidate.id}
                className="advisor-candidate"
                sx={{
                    p: 1.75,
                    borderRadius: '10px',
                    bgcolor: colors.card,
                    border: `1px solid ${colors.border}`,
                }}
            >
                <Typography className="advisor-candidate-title" sx={{ fontSize: 14.5, fontWeight: 600, color: colors.title, lineHeight: 1.7 }}>
                    {candidate.title}
                </Typography>

                {candidate.description && (
                    <Typography className="advisor-candidate-description" sx={{ fontSize: 13.5, color: colors.text, lineHeight: 1.85, mt: 0.75 }}>
                        {candidate.description}
                    </Typography>
                )}

                {candidate.link && candidate.link_text && (
                    <Box
                        component={linked ? Link : 'span'}
                        href={linked ? candidate.link : undefined}
                        className="advisor-candidate-link"
                        onClick={() => track('cta_click', {
                            cta_type: 'candidate',
                            primary_route: finalState.primary,
                            quality_tier: finalState.tier,
                        })}
                        sx={{
                            display: 'inline-block',
                            mt: 1,
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: colors.accent,
                            textDecoration: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        {candidate.link_text}
                    </Box>
                )}
            </Box>
        );
    };

    const resultPanel = (result: AdvisorResult, state: AdvisorState, answered: AdvisorQuestion[]) => {
        const resultColor = result.color || colors.accent;
        const eyebrow = result.status || resultSettings?.eyebrow;

        const why = resultSettings?.show_why === false
            ? []
            : whyLines(answered, state, Number(resultSettings?.why_limit) || 2);

        const alternative = resultSettings?.show_alternative === false || !state.secondary
            ? null
            : dimensionByKey[state.secondary] ?? null;

        const candidates = candidatesForTier(result.candidates ?? [], state.tier);
        const ctas = result.ctas ?? [];

        return (
            <>
                {eyebrow && (
                    <Box className="advisor-result-eyebrow" sx={{ fontSize: 13.5, fontWeight: 500, color: colors.muted }}>
                        {eyebrow}
                    </Box>
                )}

                <Typography className="advisor-result-title" sx={{
                    fontSize: by(v => isStacked(v) ? 24 : 23),
                    fontWeight: 700,
                    color: resultColor,
                    letterSpacing: '-.4px',
                    lineHeight: 1.6,
                    mt: 1.25,
                }}>
                    {result.title}
                </Typography>

                {result.description && (
                    <Typography className="advisor-result-description" sx={{
                        fontSize: 14.5,
                        color: colors.text,
                        lineHeight: by(v => isStacked(v) ? 1.9 : 2),
                        mt: by(v => isStacked(v) ? 1.75 : 2),
                    }}>
                        {result.description}
                    </Typography>
                )}

                {/* Same bullets as the points below it. The two blocks say different
                    things — this one repeats what the visitor told us, the points are
                    what we are advising — but each carries its own heading now, and
                    that is what has to do the separating. Two different bullet styles
                    on top of that read as two different kinds of list. */}
                {why.length > 0 && (
                    <Box className="advisor-result-why" sx={{ mt: by(v => isStacked(v) ? 2 : 2.5) }}>
                        {resultSettings?.why_title && (
                            <Typography className="advisor-result-why-title" sx={{ fontSize: 13, fontWeight: 600, color: colors.muted, mb: 1 }}>
                                {resultSettings.why_title}
                            </Typography>
                        )}
                        <Box sx={{ display: 'grid', gap: by(v => isStacked(v) ? 1.25 : 1.125) }}>
                            {why.map((line, index) => bullet(line, `why-${index}`, 'why'))}
                        </Box>
                    </Box>
                )}

                {result.points?.length > 0 && (
                    <Box className="advisor-result-points-block" sx={{ mt: by(v => isStacked(v) ? 2 : 2.5) }}>
                        {/* Named, like the why block and the candidates above and
                            below it. Two red dots under an unlabelled gap leave the
                            visitor working out for themselves whether they are
                            reading the reasoning or the advice. */}
                        {resultSettings?.points_title && (
                            <Typography className="advisor-result-points-title" sx={{ fontSize: 13, fontWeight: 600, color: colors.muted, mb: 1 }}>
                                {resultSettings.points_title}
                            </Typography>
                        )}
                        <Box className="advisor-result-points" sx={{
                            display: 'grid',
                            gap: by(v => isStacked(v) ? 1.25 : 1.125),
                            // Stacked has no panel surface behind it, so the points get
                            // their own card to stay readable on the section background.
                            bgcolor: by(v => isStacked(v) ? colors.card : 'transparent'),
                            border: by(v => isStacked(v) ? `1px solid ${colors.border}` : '0'),
                            borderRadius: by(v => isStacked(v) ? '10px' : 0),
                            p: by(v => isStacked(v) ? 1.75 : 0),
                        }}>
                            {result.points.map((point, index) => bullet(point, index, 'result'))}
                        </Box>
                    </Box>
                )}

                {alternative && (
                    <Box className="advisor-result-alternative" sx={{
                        mt: 2.5,
                        p: 1.75,
                        borderRadius: '10px',
                        border: `1px dashed ${colors.border}`,
                    }}>
                        {resultSettings?.alternative_title && (
                            <Typography className="advisor-result-alternative-title" sx={{ fontSize: 13, fontWeight: 600, color: colors.muted, mb: 0.75 }}>
                                {resultSettings.alternative_title}
                            </Typography>
                        )}
                        <Typography className="advisor-result-alternative-label" sx={{ fontSize: 14.5, fontWeight: 600, color: colors.title, lineHeight: 1.7 }}>
                            {alternative.label}
                        </Typography>
                        {alternative.description && (
                            <Typography className="advisor-result-alternative-description" sx={{ fontSize: 13.5, color: colors.text, lineHeight: 1.85, mt: 0.5 }}>
                                {alternative.description}
                            </Typography>
                        )}
                    </Box>
                )}

                {candidates.length > 0 && (
                    <Box className="advisor-result-candidates" sx={{ mt: 2.5 }}>
                        {resultSettings?.candidates_title && (
                            <Typography className="advisor-result-candidates-title" sx={{ fontSize: 13, fontWeight: 600, color: colors.muted, mb: 1 }}>
                                {resultSettings.candidates_title}
                            </Typography>
                        )}
                        <Box sx={{ display: 'grid', gap: 1.25 }}>
                            {candidates.map(candidateCard)}
                        </Box>
                    </Box>
                )}

                {(ctas.length > 0 || resultSettings?.show_reset !== false) && (
                    <Box className="advisor-result-actions" sx={{
                        // One row that wraps. The CTAs size to their labels rather
                        // than sharing out the whole width, which is what leaves
                        // room for the reset beside them once the intro column has
                        // folded away; on a narrower panel it simply wraps.
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1,
                        mt: by(v => isStacked(v) ? 2 : 'auto'),
                        pt: 2.5,
                    }}>
                        {ctas.map(cta => ctaButton(cta, resultColor))}

                        {resultSettings?.show_reset !== false && (
                            <Box
                                component="button"
                                type="button"
                                className="advisor-result-reset"
                                onClick={reset}
                                sx={{
                                    flex: '0 0 auto',
                                    whiteSpace: 'nowrap',
                                    minHeight: 46,
                                    px: 2,
                                    background: 'none',
                                    border: `1px solid ${RESET_BORDER}`,
                                    borderRadius: '8px',
                                    fontFamily: 'inherit',
                                    fontSize: 14.5,
                                    color: colors.text,
                                    cursor: 'pointer',
                                    WebkitTapHighlightColor: 'transparent',
                                    transition: 'transform .12s ease, border-color .18s',
                                    '&:active': { transform: 'scale(0.99)' },
                                    [HOVER_QUERY]: { '&:hover': { borderColor: colors.title } },
                                }}
                            >
                                {resultSettings?.reset_text || 'شروع دوباره'}
                            </Box>
                        )}
                    </Box>
                )}

                {resultSettings?.note && (
                    <Typography className="advisor-result-note" sx={{ fontSize: 11.5, color: colors.muted, lineHeight: 1.8, mt: 1.5 }}>
                        {resultSettings.note}
                    </Typography>
                )}
            </>
        );
    };

    const screenFor = (key: string): ReactNode => {
        if (key === 'idle') return idlePanel;

        if (key.startsWith('q-')) {
            const question = questions.find(item => `q-${item.key}` === key);
            return question ? questionPanel(question) : null;
        }

        const result = results.find(item => `r-${item.id}` === key);
        if (!result) return null;

        // The snapshot, when it is this result's. Reset clears the answers, and a
        // result rendered from the cleared state is a different result: no why
        // lines, no tier, and therefore every candidate instead of the ones for
        // the level the visitor picked. That happens while the screen is still
        // fading out, so the panel height chases a screen nobody is reading.
        const shown = shownResult?.result.id === result.id ? shownResult : null;

        return resultPanel(
            result,
            shown ? shown.state : finalState,
            shown ? shown.answered : answeredQuestions,
        );
    };

    // Silent copies kept only to reserve height. Because just one screen is ever
    // really on show, the panel would otherwise resize on every step; measuring
    // the tallest one this way needs no JS and no hand-set height that a longer
    // question would break. opacity:0 rather than visibility:hidden so nothing
    // inside can opt itself back in.
    //
    // The questions and the start screen reserve; the results deliberately do
    // not. A result carrying candidate cards is several times taller than any
    // question, and reserving for it would leave every question floating in the
    // top third of an enormous panel. Letting the panel grow once, on the way
    // into the result, is the one resize that reads as the answer arriving
    // rather than as a glitch — and it is the same animated height change.
    const heightReservers = [
        'idle',
        ...questions.map(question => `q-${question.key}`),
    ];

    return (
        <Box className={ROOT_CLASS} sx={{
            display: by(v => v.visible === false ? 'none' : 'block'),
            bgcolor: colors.background,
            px: by((v, key) => isStacked(v) ? 2.25 : (key === 'lg' ? 6 : 3)),
            py: by((v, key) => isStacked(v) ? 5 : (key === 'lg' ? 10 : 7)),
            // Admin overrides come last so they win over anything above.
            ...customStyleSx,
        }}>
            {/* One card, split into two halves. The panel is a half of the card
                rather than a box floating inside it, which is what made the
                earlier version read as a box within a box. */}
            <Box className={`advisor-card${introOpen ? '' : ' advisor-card-collapsed'}`} sx={{
                maxWidth,
                mx: maxWidth ? 'auto' : undefined,
                display: 'grid',
                // The intro column is only worth its half of the card while it is
                // the thing being read. Once the visitor is answering, it collapses
                // and the panel takes the whole width — the questions and the result
                // are what the card is for from then on. Track sizes are animated
                // rather than swapped so the panel grows into the space instead of
                // jumping into it.
                gridTemplateColumns: by(v => isStacked(v) ? '1fr' : (introOpen ? '1fr 1fr' : '0fr 1fr')),
                transition: reduceMotion ? 'none' : `grid-template-columns ${COLLAPSE_MS}ms ease`,
                // Makes the card a query container, which is the only way the intro
                // copy can be given the width of the column it started in without
                // measuring anything in JS. See the note on the intro body.
                containerType: by(v => isStacked(v) ? 'normal' : 'inline-size'),
                overflow: by(v => isStacked(v) ? 'visible' : 'hidden'),
                bgcolor: by(v => isStacked(v) ? 'transparent' : colors.card),
                border: by(v => isStacked(v) ? '0' : `1px solid ${colors.border}`),
                borderRadius: by(v => isStacked(v) ? 0 : '14px'),
            }}>
                <Box className="advisor-intro" sx={{
                    // Split only. In stacked the intro is rendered as the start screen
                    // inside the panel instead, so it can animate out like any other
                    // screen rather than vanishing under the transition.
                    display: by(v => isStacked(v) ? 'none' : 'flex'),
                    // A grid item refuses to go below its content's min-width unless
                    // it is told to, so without these two the column never reaches
                    // zero however small the track is asked to be.
                    minWidth: 0,
                    overflow: 'hidden',
                    // Out of the height calculation once it has finished fading. The
                    // column is invisible by then, and a long intro would otherwise
                    // keep the card as tall as copy nobody can read.
                    maxHeight: introMounted ? 'none' : 0,
                    opacity: introOpen ? 1 : 0,
                    pointerEvents: introOpen ? 'auto' : 'none',
                    transition: reduceMotion ? 'none' : `opacity ${COLLAPSE_MS}ms ease`,
                }}>
                    {/* Held at the width of the column it started in, and clipped by
                        the column rather than reflowed inside it. A text column that
                        is allowed to reflow as it narrows ends up one character per
                        line — several times taller than the card — and the card grows
                        to match while the column it came from is on its way out. */}
                    <Box className="advisor-intro-body" sx={{
                        flex: 'none',
                        width: by(v => isStacked(v) ? '100%' : '50cqw'),
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        p: by((v, key) => isStacked(v) ? 0 : (key === 'lg' ? 5.5 : 3.5)),
                    }}>
                        {introContent}
                    </Box>
                </Box>

                {/* Three layers on purpose: the outer stretches to the card so the
                    panel background fills its half, the middle is the height being
                    animated and clips what overflows it, and the inner is left at
                    its natural height to be measured. */}
                <Box className="advisor-panel" sx={{
                    overflow: 'hidden',
                    bgcolor: by(v => isStacked(v) ? 'transparent' : colors.panel),
                    borderInlineStartStyle: 'solid',
                    borderInlineStartColor: colors.border,
                    // The divider belongs to the intro column. Once that column is
                    // gone the line would sit on top of the card's own border, so it
                    // thins away on the same curve.
                    borderInlineStartWidth: by(v => (isStacked(v) || !introOpen) ? 0 : '1px'),
                    transition: reduceMotion ? 'none' : `border-inline-start-width ${COLLAPSE_MS}ms ease`,
                }}>
                    <AnimatedBox
                        className="advisor-panel-viewport"
                        style={contentHeight === null ? undefined : heightSpring}
                    >
                        <Box ref={contentRef} className="advisor-panel-inner" sx={{
                            display: 'grid',
                            minHeight: by(v => isStacked(v) ? 'auto' : 392),
                            p: by((v, key) => isStacked(v) ? 0 : (key === 'lg' ? 5.5 : 3.5)),
                            // Full width is more room than a question wants. Once the
                            // intro column is out of the way the content keeps a
                            // readable measure and centres in what is left — the extra
                            // space goes to the panel, not to the line length.
                            maxWidth: by(v => isStacked(v) ? 'none' : PANEL_MEASURE),
                            mx: by(v => isStacked(v) ? 0 : 'auto'),
                            '& > *': { gridArea: '1 / 1', minWidth: 0 },
                        }}>
                            {heightReservers.map(key => (
                                <Box
                                    key={`reserve-${key}`}
                                    className="advisor-screen advisor-screen-reserved"
                                    aria-hidden
                                    sx={{
                                        // Split only. There the panel sits beside a
                                        // fixed intro column, so a card that resizes
                                        // is the thing that looks wrong; stacked reads
                                        // better following its live screen.
                                        display: by(v => isStacked(v) ? 'none' : 'flex'),
                                        flexDirection: 'column',
                                        opacity: 0,
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {screenFor(key)}
                                </Box>
                            ))}

                            {transitions((style, key) => {
                                // On the way back, the screen being left has to stop
                                // holding the panel open or the height cannot start
                                // falling until it unmounts — which is what made the
                                // start screen arrive before the card had shrunk. It
                                // keeps painting and fading; it just contributes no
                                // height, and the panel clips whatever the falling
                                // height leaves behind. Only on the way back: doing
                                // it on every hand-over would clip a question that is
                                // still perfectly readable.
                                const detach = key !== currentKey && currentKey === 'idle';

                                return (
                                    <AnimatedBox
                                        style={style}
                                        className={`advisor-screen advisor-screen-active${detach ? ' advisor-screen-leaving' : ''}`}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            maxHeight: detach ? 0 : 'none',
                                        }}
                                    >
                                        {screenFor(key)}
                                    </AnimatedBox>
                                );
                            })}
                        </Box>
                    </AnimatedBox>
                </Box>
            </Box>
        </Box>
    );
});

export default AdvisorLC;
